import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useOrgBoards } from '../../../hooks/useOrgIssues.js';
import { useCreateTeamMeetingSeries } from '../../../hooks/useTeamMeetings.js';
import type {
  OwnerCandidates,
  SeriesCreateRequest,
  SeriesView,
} from '../../../lib/api/team-meetings.js';

/** 画面で選べる所有組織の種類（人を指定する所有は MCP から作る）。 @graph-connects none */
const OWNER_KINDS = ['team', 'group', 'division'] as const;

/** 画面で選べる公開範囲（人を指定する公開範囲は MCP から作る）。 @graph-connects none */
const VISIBILITY_KINDS = ['team', 'group', 'division', 'company', 'leaders'] as const;

/**
 * 見られるシリーズを並べ、開催回の画面へ移る入口にする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分が見られる週次MTG のシリーズを所有組織・公開範囲と一緒に並べ、開催回の画面へ移る入口にする
 * @graph-connects none
 */
export function SeriesCards({ series }: { series: readonly SeriesView[] }): ReactNode {
  const { t } = useTranslation();
  if (series.length === 0) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">{t('teamMeetings.series.empty')}</p>
    );
  }
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {series.map(({ series: s }) => (
        <Link
          key={s.id}
          to="/team-meetings/$seriesId"
          params={{ seriesId: s.id }}
          className="block hover:opacity-80"
        >
          <Card>
            <CardContent className="space-y-1 p-4">
              <p className="font-semibold text-[var(--color-text-primary)]">{s.name}</p>
              <p className="text-xs text-[var(--color-text-muted)]">
                {s.owner.kind === 'users'
                  ? t('teamMeetings.series.ownerUsers')
                  : `${t(`teamMeetings.orgKind.${s.owner.kind}`)}: ${s.owner.code}`}
                {' / '}
                {t(`teamMeetings.visibility.${s.visibility.kind}`)}
              </p>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

/**
 * シリーズを作る（所有組織のリーダーと admin。判定は API）。作ったら開催回の画面へ移る。
 * 所有・公開範囲は組織単位だけを出す（人を指定する形は MCP から作る）。所有組織は API が返す
 * 候補（自分がリーダーの組織）から選ぶ。候補が 1 つも無ければフォームを出さずに理由を出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のシリーズを名前・所有組織・公開範囲・既定のボードを指定して作り、作ったシリーズの開催回の画面へ移る
 * @graph-connects useCreateTeamMeetingSeries [calls] シリーズの作成
 * @graph-connects useOrgBoards [calls] 既定のボードの候補
 */
export function CreateSeriesForm({
  candidates,
  onCancel,
}: {
  candidates: OwnerCandidates;
  onCancel: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateTeamMeetingSeries();
  const boards = useOrgBoards();
  const ownerKinds = OWNER_KINDS.filter((kind) => candidates[kind].length > 0);
  const [name, setName] = useState('');
  const [ownerKind, setOwnerKind] = useState<(typeof OWNER_KINDS)[number]>(ownerKinds[0] ?? 'team');
  const [ownerCode, setOwnerCode] = useState(candidates[ownerKind][0] ?? '');
  const [visibility, setVisibility] = useState<(typeof VISIBILITY_KINDS)[number]>('division');
  const [defaultBoardId, setDefaultBoardId] = useState('');

  if (ownerKinds.length === 0) {
    return (
      <Card>
        <CardContent className="space-y-3 p-4">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('teamMeetings.create.noOwner')}
          </p>
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            {t('teamMeetings.common.cancel')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            const input: SeriesCreateRequest = {
              name,
              owner: { kind: ownerKind, code: ownerCode },
              visibility: { kind: visibility },
              defaultBoardId: defaultBoardId === '' ? null : defaultBoardId,
            };
            create.mutate(input, {
              onSuccess: (view) => {
                void navigate({
                  to: '/team-meetings/$seriesId',
                  params: { seriesId: view.series.id },
                });
              },
            });
          }}
        >
          <Input
            label={t('teamMeetings.create.name')}
            placeholder={t('teamMeetings.create.namePlaceholder')}
            value={name}
            fullWidth
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <Select
              label={t('teamMeetings.create.ownerKind')}
              value={ownerKind}
              options={ownerKinds.map((value) => ({
                value,
                label: t(`teamMeetings.orgKind.${value}`),
              }))}
              onChange={(e) => {
                const kind = ownerKinds.find((k) => k === e.target.value) ?? ownerKind;
                setOwnerKind(kind);
                setOwnerCode(candidates[kind][0] ?? '');
              }}
            />
            <Select
              label={t('teamMeetings.create.ownerCode')}
              helperText={t('teamMeetings.create.ownerCodeHelp')}
              value={ownerCode}
              options={candidates[ownerKind].map((code) => ({ value: code, label: code }))}
              onChange={(e) => {
                setOwnerCode(e.target.value);
              }}
            />
            <Select
              label={t('teamMeetings.create.visibility')}
              value={visibility}
              options={VISIBILITY_KINDS.map((value) => ({
                value,
                label: t(`teamMeetings.visibility.${value}`),
              }))}
              onChange={(e) => {
                setVisibility(VISIBILITY_KINDS.find((k) => k === e.target.value) ?? 'division');
              }}
            />
            <Select
              label={t('teamMeetings.create.defaultBoard')}
              helperText={t('teamMeetings.create.defaultBoardHelp')}
              value={defaultBoardId}
              options={[
                { value: '', label: t('teamMeetings.create.noBoard') },
                ...(boards.data?.boards ?? []).map(({ board }) => ({
                  value: board.id,
                  label: board.name,
                })),
              ]}
              onChange={(e) => {
                setDefaultBoardId(e.target.value);
              }}
            />
          </div>
          {create.isError && (
            <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
              {create.error.message}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" size="sm" loading={create.isPending}>
              {t('teamMeetings.create.submit')}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
              {t('teamMeetings.common.cancel')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
